import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Moon, Check, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { localISODate, formatDate, LONG_DATE } from "@/lib/dates";
import { useI18n } from "@/context/I18nContext";
import { SectionHeader, EmptyState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

const today = () => localISODate();

export default function Sleep() {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const [hours, setHours] = useState("");
  const [quality, setQuality] = useState(4);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: logs } = useQuery({ queryKey: ["sleep"], queryFn: async () => (await api.get("/sleep")).data });

  const save = async () => {
    if (!hours) return toast.error(t("sleepHours"));
    setSaving(true);
    try {
      await api.post("/sleep", { hours: parseFloat(hours), quality, notes, date: today() });
      toast.success(t("save"));
      setHours(""); setNotes(""); setQuality(4);
      qc.invalidateQueries({ queryKey: ["sleep"] });
    } catch { toast.error("Error"); } finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <SectionHeader title={t("sleep")} subtitle={formatDate(new Date(), lang, LONG_DATE)} />

      <div className="rounded-2xl border bg-card p-5" data-testid="sleep-form">
        <div className="mb-4 flex items-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary/15 text-primary"><Moon className="h-5 w-5" /></span>
          <h3 className="font-display font-semibold">{t("logSleep")}</h3>
        </div>
        <div className="space-y-4">
          <div>
            <Label>{t("sleepHours")}</Label>
            <Input type="number" step="0.5" inputMode="decimal" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="7.5" className="mt-1.5 tabular-nums" data-testid="sleep-hours" />
          </div>
          <div>
            <Label className="mb-2 block">{t("sleepQualityLabel")}</Label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => setQuality(n)} data-testid={`sleep-quality-${n}`}
                  className={cn("h-11 flex-1 rounded-xl border font-display text-lg font-semibold tabular-nums transition-all duration-200",
                    quality === n ? "border-primary bg-primary text-primary-foreground scale-105" : "hover:bg-accent")}>
                  {n}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>{t("notes")}</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1.5" data-testid="sleep-notes" />
          </div>
          <Button onClick={save} disabled={saving} className="w-full gap-2" data-testid="sleep-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {t("save")}
          </Button>
        </div>
      </div>

      <div>
        <h3 className="mb-3 font-display font-semibold">{t("recentSleep")}</h3>
        {logs?.length ? (
          <div className="space-y-2" data-testid="sleep-list">
            {logs.map((s) => (
              <div key={s.id} className="flex items-center justify-between rounded-xl border bg-card px-4 py-3" data-testid={`sleep-${s.id}`}>
                <div>
                  <div className="font-display font-semibold tabular-nums">{s.hours} {t("sleepHours").includes("jam") ? "jam" : "h"}</div>
                  <div className="text-xs text-muted-foreground">{formatDate(s.date, lang)}{s.notes ? ` · ${s.notes}` : ""}</div>
                </div>
                <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">{t("sleepQualityLabel")} {s.quality}/5</span>
              </div>
            ))}
          </div>
        ) : <EmptyState icon={Moon} title={t("sleep")} description={t("noSleep")} />}
      </div>
    </div>
  );
}
